import { forwardRef, useImperativeHandle } from "react";
import styled from "styled-components";
import CardGridSkeleton from "../gridCards/animations/CardGridSkeleton";
import { v4 as uuidv4 } from "uuid";
import React, { useEffect, useRef, useState } from "react";
import StoresGridCard from "../gridCards/StoresGridCard";
import * as Library from "../../../utils/Library";
import { useNavigate } from "react-router";
import { AppQueryClient } from "../../../api/queryClient";

/* #region  SCOPED CSS */
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -24px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 17px;
    }
`;
/* #endregion */

/**
 * Renders an infinite grid of store cards.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className=""] - Additional CSS class name for the component.
 * @param {number} [props.limit=10] - The number of cards to fetch per page.
 * @param {string} [props.queryKey] - The query key for the infinite query component.
 * @param {number} [props.minColumnWidth=395] - The minimum width of each grid column.
 * @param {number} [props.minimumCardsFill=10] - The minimum number of cards to fill the grid.
 * @param {Function} [props.onEditAction] - Callback function when the edit button is clicked.
 * @param {Function} [props.onDeleteAction] - Callback function when the delete button is clicked.
 * @param {Function} [props.onSuspendAction] - Callback function when the suspend button is clicked.
 * @param {string} [props.statusFromPath] - The status from the path.
 * @returns {JSX.Element} The rendered component.
 */
export default forwardRef(function InfinityGridStoresCards(props, ref) {
    const navigate = useNavigate();
    const pageLimit = props?.limit || 10;

    const gridLayoutStyle = {
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${props?.minColumnWidth || "395px"}, 1fr))`,
        gap: "24px"
    };

    /* #region STATES */
    const scrollContainerRef = useRef(null);
    const [scroll, setScroll] = useState(true);
    const [minimumCardsFill, setminimumCardsFill] = useState(1); //props?.minimumCardsFill || pageLimit
    const requestEnabled = props?.requestEnabled ?? true;

    const [queryKey, setQueryKey] = useState(props?.queryKey ?? uuidv4());

    useEffect(() => {
        if (props?.queryKey) setQueryKey(props?.queryKey);
    }, [props?.queryKey]);

    const filters = props?.filters;

    const {
        data,
        isFetching: isLoading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = Library.usePostInfiniteQuery(
        queryKey,
        "getStoresData",
        {
            companyID: localStorage.getItem("currentCompanyID"),
            limit: pageLimit,
            ...filters
        },
        () => {
            setScroll(true);
        },
        true,
        "Stores",
        false,
        requestEnabled
    );

    const pageList = data?.pages;
    //const totalCount = data?.pages[0]?.data?.totalCount;
    const nextDataCount = data?.pages[data?.pages.length - 1].data?.nextDataCount;
    /* #endregion */

    /* #region EFFECTS */
    useEffect(() => {
        handleScroll();

        setminimumCardsFill(getTotalCards() + 1);
    }, [data?.pages]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer && !isLoading && scroll && !isFetchingNextPage) {
            //console.log("scroll event added");
            scrollContainer.addEventListener("scroll", handleScroll);
        }

        return () => {
            //console.log("scroll event removed");
            scrollContainer?.removeEventListener("scroll", handleScroll);
        };
    }, [scrollContainerRef, isLoading, pageList, scroll, isFetchingNextPage, data?.pages]);
    /* #endregion */

    /* #region METHODS */
    const handleScroll = () => {
        const scrollContainer = scrollContainerRef.current;

        const scrolledRatio = scrollContainer.scrollTop / (scrollContainer.scrollHeight - scrollContainer.clientHeight);
        const isAtBottom = scrolledRatio >= 0.6;

        if (isAtBottom && !isLoading && hasNextPage && !isFetchingNextPage) {
            setScroll(false);
            fetchNextPage();
        }
    };

    const getTotalCards = () => {
        return pageList?.reduce((acc, it) => {
            return acc + it?.data?.result?.length;
        }, 0);
    };

    const getStatusValue = row => {
        const statusFromPath = props?.statusFromPath;
        const status = getArrayFieldValue(row, statusFromPath.split(".") || "Unknown");
        //console.log("status", status);

        //TODO: This is a temporary fix for the "Not Connected" status value
        if (status === "Not Connected") return "Not connected";

        return status;
    };

    const getArrayFieldValue = (row, array) => {
        if (!Array.isArray(array)) return "Unknown";

        let res = row;
        for (let i = 0; i < array.length; i++) {
            res = res?.[array[i]];
        }
        return res;
    };

    const connectPos = item => {
        if (item?.POSstatusFrom === "Store") {
            navigate(`/stores/edit/${item?.ourID}#stock-sharing`);
            sessionStorage.setItem("scrollToPOS", true);
        } else if (item?.POSstatusFrom === "Retail Brand") {
            navigate(`/stores/pos?connectStoreID=${item?.id}`);
        }
    };

    const handleReset = infiniteGridQuerykey => {
        scrollContainerRef.current.scrollTop = 0;
        setScroll(true);
        AppQueryClient.clear();

        const newQueryKeyTMP = [...infiniteGridQuerykey];
        newQueryKeyTMP["refresh"] = uuidv4();
        setQueryKey(newQueryKeyTMP);
    };
    /* #endregion */

    useImperativeHandle(ref, () => ({
        reset: handleReset
    }));

    return (
        <StyledPage className={`library-scrollable ${props?.className || ""}`} ref={scrollContainerRef}>
            {isLoading && !isFetchingNextPage ? (
                <div style={gridLayoutStyle}>
                    {Array.from({ length: 8 }, () => (
                        <CardGridSkeleton key={uuidv4()} height="483.28px" />
                    ))}
                </div>
            ) : (
                <div className="mb-24px" style={gridLayoutStyle}>
                    {pageList?.map(it => {
                        return it?.data?.result?.map(item => {
                            const restrictedStoreIDs = JSON.parse(localStorage.getItem("restrictedStoreIDs") || "[]");
                            const canEditAccess = props?.editAccess || restrictedStoreIDs.includes(item?.id);
                            return (
                                <StoresGridCard
                                    key={item?.ourID}
                                    image={item?.logoURL}
                                    title={item?.name}
                                    id={item?.ourID}
                                    isCardEnabled={getStatusValue(item)?.toLowerCase() === "active"}
                                    isCardSuspended={getStatusValue(item)?.toLowerCase() === "suspended"}
                                    data={item}
                                    edit={
                                        props?.onEditAction && canEditAccess
                                            ? { onClick: () => props?.onEditAction(item) }
                                            : null
                                    }
                                    delete={
                                        props?.onDeleteAction ? { onClick: () => props?.onDeleteAction(item) } : null
                                    }
                                    suspend={{
                                        isReenable: item?.status?.toLowerCase() === "suspended",
                                        onClick: props?.onSuspendAction ? () => props?.onSuspendAction(item) : null
                                    }}
                                    primaryButton={{
                                        title: item?.POSstatus === "Not connected" ? "CONNECT POS" : "DETAILS",
                                        onClick:
                                            item?.POSstatus === "Not connected" ? () => connectPos(item) : undefined
                                    }}
                                    secondaryButton={{ title: "Add Staff" }}
                                    POSstatus={item?.POSstatus}
                                />
                            );
                        });
                    })}

                    {!isFetchingNextPage &&
                        minimumCardsFill - getTotalCards() > 0 &&
                        Array.from({ length: minimumCardsFill - getTotalCards() }, (_, index) => (
                            <StoresGridCard isCardEnabled={false} key={index} />
                        ))}

                    {isFetchingNextPage &&
                        Array.from({ length: nextDataCount }, (_, index) => (
                            <CardGridSkeleton height="483.28px" key={index} />
                        ))}
                </div>
            )}
        </StyledPage>
    );
});
